import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Check, Plus, Trash2, TriangleAlert } from 'lucide-react';
import api from '@/lib/api';
import Preloader from '@/components/Preloader';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { usePode } from '@/stores/authStore';
import VerbasDaFolha from './VerbasDaFolha';
import { erroDaApi, type Vinculos } from './tipos';

type Rubrica = Vinculos['rubricas'][number];

const RUBRICA_NOVA = { codigo: '', descricao: '', tipo: 'provento' as Rubrica['tipo'], destino: '' };

const TIPO: Record<Vinculos['rubricas'][number]['tipo'], string> = {
  provento: 'Provento',
  desconto: 'Desconto',
  informativo: 'Informativo',
};

/**
 * O destino de cada rubrica da folha: a verba contábil em que ela soma, ou
 * "não entra na planilha" — como o adiantamento, que já foi lançado quando pago.
 *
 * É cadastro, não passo do mês: "a partir do momento que surgiu uma situação
 * nova, configurou — vai ser sempre a mesma coisa". Rubrica nova aparece no
 * topo, a classificar.
 */
export default function RubricasPage() {
  const podeConfigurar = usePode('folha.configurar');
  const queryClient = useQueryClient();
  const [parametros, setParametros] = useSearchParams();
  const aba = parametros.get('aba') === 'verbas' ? 'verbas' : 'rubricas';
  const [nova, setNova] = useState(RUBRICA_NOVA);
  const [excluindo, setExcluindo] = useState<Rubrica | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['folha-vinculos'],
    queryFn: async () => (await api.get<{ data: Vinculos }>('/folha/vinculos')).data.data,
  });

  const salvar = useMutation({
    mutationFn: async ({ id, verba }: { id: number; verba: number | null }) =>
      (
        await api.put(
          `/folha/rubricas/${id}`,
          verba === null ? { ignorar: true, motivo: 'Não entra na planilha do ERP' } : { ignorar: false, folha_verba_id: verba },
        )
      ).data,
    // O servidor recalcula as competências abertas: o que depende disso fica velho.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['folha-vinculos'] });
      void queryClient.invalidateQueries({ queryKey: ['folha-verbas'] });
      void queryClient.invalidateQueries({ queryKey: ['folha'] });
      void queryClient.invalidateQueries({ queryKey: ['folha-conferencia'] });
      void queryClient.invalidateQueries({ queryKey: ['folha-lancamentos'] });
    },
  });

  const recarregar = () => {
    void queryClient.invalidateQueries({ queryKey: ['folha-vinculos'] });
    void queryClient.invalidateQueries({ queryKey: ['folha-verbas'] });
  };

  // A rubrica que o escritório avisou que vai mandar: quando a folha chegar com
  // ela, o valor já cai na verba escolhida.
  const cadastrar = useMutation({
    mutationFn: async () =>
      (
        await api.post('/folha/rubricas', {
          codigo: nova.codigo,
          descricao: nova.descricao,
          tipo: nova.tipo,
          ...(nova.destino === 'ignorar'
            ? { ignorar: true, motivo: 'Não entra na planilha do ERP' }
            : { ignorar: false, folha_verba_id: nova.destino === '' ? null : Number(nova.destino) }),
        })
      ).data,
    onSuccess: () => {
      setNova(RUBRICA_NOVA);
      recarregar();
    },
  });

  const excluir = useMutation({
    mutationFn: async (rubrica: Rubrica) => (await api.delete(`/folha/rubricas/${rubrica.id}`)).data,
    onSuccess: () => {
      setExcluindo(null);
      recarregar();
    },
  });

  // As que faltam classificar vêm primeiro; o resto, na ordem do código.
  const rubricas = useMemo(
    () =>
      [...(data?.rubricas ?? [])].sort(
        (a, b) => Number(a.classificada) - Number(b.classificada) || a.codigo.localeCompare(b.codigo),
      ),
    [data],
  );

  const cabecalho = (subtitulo: string) => (
    <header className="mb-md">
      <h1 className="text-[22px] font-bold text-ink">Rubricas e verbas</h1>
      <p className="mt-xs text-[13px] text-muted">{subtitulo}</p>

      <div role="tablist" className="mt-md flex gap-md border-b border-border">
        {(['rubricas', 'verbas'] as const).map((opcao) => (
          <button
            key={opcao}
            type="button"
            role="tab"
            aria-selected={aba === opcao}
            onClick={() => setParametros(opcao === 'rubricas' ? {} : { aba: opcao }, { replace: true })}
            className={`-mb-px border-b-2 px-xs pb-sm text-[14px] font-bold ${
              aba === opcao ? 'border-accent text-ink' : 'border-transparent text-muted hover:text-ink'
            }`}
          >
            {opcao === 'rubricas' ? 'Rubricas' : 'Verbas'}
          </button>
        ))}
      </div>
    </header>
  );

  if (aba === 'verbas') {
    return (
      <div className="max-w-[1100px] px-lg py-lg">
        {cabecalho('Criar, editar ou excluir verba recalcula as competências que ainda não foram para o ERP.')}
        <VerbasDaFolha />
      </div>
    );
  }

  if (isLoading) {
    return <Preloader telaCheia />;
  }

  if (isError || !data) {
    return <p className="px-lg py-lg text-[14px] text-danger">Não foi possível carregar as rubricas.</p>;
  }

  const pendentes = rubricas.filter((rubrica) => !rubrica.classificada).length;

  return (
    <div className="max-w-[1100px] px-lg py-lg">
      {cabecalho(
        pendentes === 0
          ? 'Toda rubrica da folha tem destino. Mudar aqui recalcula as competências que ainda não foram para o ERP.'
          : `${pendentes} ${pendentes === 1 ? 'rubrica nova segura' : 'rubricas novas seguram'} a planilha do ERP até ser classificada.`,
      )}

      {podeConfigurar && (
        <p className="mb-sm text-[13px] text-muted">
          Se nenhuma verba serve para a rubrica,{' '}
          <button type="button" onClick={() => setParametros({ aba: 'verbas' })} className="font-bold text-accent hover:underline">
            crie uma nova na aba Verbas
          </button>
          .
        </p>
      )}

      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <table className="w-full text-[13px]">
          <thead className="border-b-2 border-border bg-surface-subtle text-[11px] tracking-wide text-ink-soft uppercase">
            <tr>
              <th className="px-sm py-sm text-left font-bold">Rubrica</th>
              <th className="px-sm py-sm text-left font-bold">Tipo</th>
              <th className="px-sm py-sm text-left font-bold">Verba contábil</th>
              <th className="w-[64px] px-sm py-sm" />
            </tr>
          </thead>
          <tbody>
            {rubricas.map((rubrica) => (
              <tr key={rubrica.id} className="border-b border-border last:border-0 even:bg-dominant">
                <td className="px-sm py-xs">
                  <span className="font-bold text-ink tabular-nums">{rubrica.codigo}</span>
                  <span className="ml-sm text-ink">{rubrica.descricao}</span>
                  {!rubrica.na_folha && (
                    <span className="ml-sm rounded-full bg-accent/10 px-sm py-[1px] text-[11px] font-bold text-accent">
                      ainda não veio na folha
                    </span>
                  )}
                </td>
                <td className="px-sm py-xs text-muted">{TIPO[rubrica.tipo]}</td>
                <td className="w-[42%] px-sm py-xs">
                  <select
                    value={rubrica.ignorar ? 'ignorar' : (rubrica.folha_verba_id ?? '')}
                    disabled={!podeConfigurar || salvar.isPending}
                    onChange={(evento) =>
                      salvar.mutate({
                        id: rubrica.id,
                        verba: evento.target.value === 'ignorar' ? null : Number(evento.target.value),
                      })
                    }
                    className={`h-8 w-full rounded-md border bg-surface px-sm text-[13px] ${
                      rubrica.classificada ? 'border-border text-ink' : 'border-warning text-warning'
                    }`}
                  >
                    <option value="">a classificar…</option>
                    <option value="ignorar">não entra na planilha</option>
                    {data.verbas.map((verba) => (
                      <option key={verba.id} value={verba.id}>
                        {verba.nome}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-sm py-xs text-right whitespace-nowrap">
                  {podeConfigurar && !rubrica.na_folha && (
                    <button
                      type="button"
                      onClick={() => {
                        excluir.reset();
                        setExcluindo(rubrica);
                      }}
                      title="Excluir: a rubrica ainda não veio em nenhuma folha"
                      className="mr-xs rounded-md p-[4px] align-middle text-muted hover:bg-danger/10 hover:text-danger"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                  {rubrica.classificada ? (
                    <Check size={15} className="inline align-middle text-success" />
                  ) : (
                    <TriangleAlert size={15} className="inline align-middle text-warning" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {podeConfigurar && (
        <form
          onSubmit={(evento) => {
            evento.preventDefault();
            cadastrar.mutate();
          }}
          className="mt-md rounded-lg border border-border bg-surface p-md"
        >
          <h2 className="text-[14px] font-bold text-ink">Cadastrar rubrica antes da folha chegar</h2>
          <p className="mt-[2px] text-[12px] text-muted">
            Quando o escritório avisar de uma rubrica nova, ela já pode ter destino: a folha que a trouxer soma o valor na
            verba escolhida.
          </p>

          <div className="mt-sm flex flex-wrap items-end gap-sm">
            <label className="w-[80px]">
              <span className="block text-[12px] font-bold text-muted">Código</span>
              <input
                value={nova.codigo}
                onChange={(evento) => setNova((atual) => ({ ...atual, codigo: evento.target.value }))}
                inputMode="numeric"
                maxLength={3}
                required
                className="mt-[2px] h-9 w-full rounded-md border border-border bg-surface px-sm text-[13px] text-ink tabular-nums"
              />
            </label>
            <label className="min-w-[200px] flex-1">
              <span className="block text-[12px] font-bold text-muted">Descrição</span>
              <input
                value={nova.descricao}
                onChange={(evento) => setNova((atual) => ({ ...atual, descricao: evento.target.value }))}
                maxLength={60}
                required
                className="mt-[2px] h-9 w-full rounded-md border border-border bg-surface px-sm text-[13px] text-ink"
              />
            </label>
            <label className="w-[130px]">
              <span className="block text-[12px] font-bold text-muted">Tipo</span>
              <select
                value={nova.tipo}
                onChange={(evento) => setNova((atual) => ({ ...atual, tipo: evento.target.value as Rubrica['tipo'] }))}
                className="mt-[2px] h-9 w-full rounded-md border border-border bg-surface px-sm text-[13px] text-ink"
              >
                {Object.entries(TIPO).map(([valor, rotulo]) => (
                  <option key={valor} value={valor}>
                    {rotulo}
                  </option>
                ))}
              </select>
            </label>
            <label className="min-w-[240px] flex-1">
              <span className="block text-[12px] font-bold text-muted">Verba contábil</span>
              <select
                value={nova.destino}
                onChange={(evento) => setNova((atual) => ({ ...atual, destino: evento.target.value }))}
                required
                className="mt-[2px] h-9 w-full rounded-md border border-border bg-surface px-sm text-[13px] text-ink"
              >
                <option value="">escolher…</option>
                <option value="ignorar">não entra na planilha</option>
                {data.verbas.map((verba) => (
                  <option key={verba.id} value={verba.id}>
                    {verba.nome}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="submit"
              disabled={cadastrar.isPending}
              className="flex h-9 items-center gap-sm rounded-lg bg-accent px-md text-[13px] font-bold text-white hover:bg-accent/90 disabled:opacity-50"
            >
              <Plus size={15} />
              Cadastrar
            </button>
          </div>

          {cadastrar.isError && (
            <p className="mt-sm text-[13px] font-bold text-danger">{erroDaApi(cadastrar.error, 'Não foi possível cadastrar a rubrica.')}</p>
          )}
        </form>
      )}

      <Dialog open={excluindo !== null} onOpenChange={(aberto) => !excluir.isPending && !aberto && setExcluindo(null)}>
        <DialogContent className="max-w-[460px] p-lg">
          <DialogTitle>
            Excluir a rubrica {excluindo?.codigo} {excluindo?.descricao}?
          </DialogTitle>
          <DialogDescription className="mt-xs">
            Ela ainda não veio em nenhuma folha. Se o escritório mandá-la depois, volta como rubrica a classificar.
          </DialogDescription>
          {excluir.isError && (
            <p className="mt-sm text-[13px] font-bold text-danger">{erroDaApi(excluir.error, 'Não foi possível excluir a rubrica.')}</p>
          )}
          <div className="mt-lg flex justify-end gap-sm">
            <button
              type="button"
              onClick={() => setExcluindo(null)}
              disabled={excluir.isPending}
              className="rounded-md border border-border px-md py-sm text-[13px] font-bold text-ink hover:bg-surface-subtle"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => excluindo && excluir.mutate(excluindo)}
              disabled={excluir.isPending}
              className="inline-flex items-center gap-xs rounded-md bg-danger px-md py-sm text-[13px] font-bold text-white hover:bg-danger/90 disabled:opacity-50"
            >
              <Trash2 size={14} aria-hidden="true" />
              {excluir.isPending ? 'Excluindo…' : 'Excluir rubrica'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
